/**
 * The HubSpot meetings embed posts `{ meetingBookSucceeded: true, meetingsPayload }` to the
 * parent window when a booking completes. The payload isn't formally documented, so the
 * start time is read defensively from the known locations and degrades to "date unknown".
 */

export interface MeetingBooking {
    startISO: string | null;
}

const toISO = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null;
    const asNumber = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
    if (typeof asNumber !== 'number' && typeof asNumber !== 'string') return null;
    const date = new Date(asNumber);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const parseMeetingBookedMessage = (
    event: { origin: string; data: unknown },
    allowedOrigins: readonly string[],
): MeetingBooking | null => {
    if (!allowedOrigins.includes(event.origin)) return null;
    const data = event.data as any;
    if (!data || typeof data !== 'object' || data.meetingBookSucceeded !== true) return null;

    const bookingResponse = data.meetingsPayload?.bookingResponse;
    const candidates = [bookingResponse?.event?.dateTime, bookingResponse?.postResponse?.timerange?.start];
    for (const candidate of candidates) {
        const iso = toISO(candidate);
        if (iso) return { startISO: iso };
    }
    return { startISO: null };
};

/**
 * The embed also posts its content height (`{ height }`, object or JSON string) so the host can size
 * the iframe to fit — the same thing HubSpot's own embed script does. Returns the height in px or null.
 */
export const parseMeetingHeightMessage = (
    event: { origin: string; data: unknown },
    allowedOrigins: readonly string[],
): number | null => {
    if (!allowedOrigins.includes(event.origin)) return null;
    let data: any = event.data;
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch {
            return null;
        }
    }
    const height = data && typeof data === 'object' ? data.height : undefined;
    return typeof height === 'number' && Number.isFinite(height) && height > 0 ? height : null;
};

export const buildMeetingEmbedUrl = (
    baseUrl: string,
    user: { firstName?: string; lastName?: string; email?: string },
): string | null => {
    if (!baseUrl) return null;
    let url: URL;
    try {
        url = new URL(baseUrl);
    } catch {
        return null;
    }
    url.searchParams.set('embed', 'true');
    (['firstName', 'lastName', 'email'] as const).forEach(key => {
        if (user[key]) url.searchParams.set(key, user[key]!);
    });
    return url.toString();
};

/**
 * HubSpot's scheduler layout, measured from its embed page (it can't be styled from outside):
 * - Phone layout (frames narrower than ~630px): a card `min(width - 8, 400)` wide with a 4px side margin,
 *   which stretches to the frame's full height.
 * - Desktop layout: a card `width - 8` wide (850px at most, then centred) and a fixed height, inset
 *   16px from the top; the height it reports runs 29px past the card's bottom edge.
 * Neither layout can be made to fit by height alone, so the frame is rendered at the size HubSpot needs
 * and scaled (CSS transform) to the space available, then cropped to the card. The result never
 * scrolls; only HubSpot's own form section does.
 */
export const MEETING_CARD = {
    sideMargin: 4,
    phoneMaxWidth: 400,
    desktopMinWidth: 632,
    desktopMaxWidth: 850,
    desktopTop: 16,
    desktopBottom: 29,
    /** Height HubSpot reports on desktop before it has said so itself. */
    desktopReportedHeight: 715,
} as const;

/** Never shrink the scheduler below this; past it the modal scrolls instead. */
export const MIN_MEETING_SCALE = 0.6;

const desktopCardHeight = (reportedHeight: number | null) =>
    (reportedHeight ?? MEETING_CARD.desktopReportedHeight) - MEETING_CARD.desktopTop - MEETING_CARD.desktopBottom;

/**
 * Desktop modal width. When the scheduler is scaled down to fit a short screen, the modal narrows by the
 * same factor; otherwise HubSpot's card would hit its max width and leave blank strips at the sides.
 */
export const meetingModalWidth = ({ preferredWidth, availableHeight, reportedHeight }: {
    preferredWidth: number;
    availableHeight: number;
    reportedHeight: number | null;
}): number => {
    const heightScale = Math.max(MIN_MEETING_SCALE, Math.min(1, availableHeight / desktopCardHeight(reportedHeight)));
    return Math.min(preferredWidth, Math.floor(MEETING_CARD.desktopMaxWidth * heightScale));
};

export interface MeetingFrameFit {
    scale: number;
    /** Unscaled iframe size and offset inside the cropping box. */
    frameWidth: number | null;
    frameHeight: number;
    top: number;
    left: number;
    /** Visible (cropped, scaled) height. */
    boxHeight: number;
    /** False only when the screen is too short even at the minimum scale; the modal must scroll then. */
    fitsHeight: boolean;
}

export const fitMeetingFrame = ({ width, availableHeight, phone, reportedHeight }: {
    /** Width of the box the scheduler is shown in; 0 before it has been measured. */
    width: number;
    /** Height the scheduler may take without making the modal scroll. */
    availableHeight: number;
    /** Full-screen phone layout (fill the height) rather than the desktop card. */
    phone: boolean;
    reportedHeight: number | null;
}): MeetingFrameFit => {
    const { sideMargin, phoneMaxWidth, desktopMinWidth, desktopTop, desktopBottom, desktopReportedHeight } = MEETING_CARD;

    if (phone) {
        // Widen the card to the screen: up to 400px it already fits, past that scale it up.
        const cardWidth = width > 0 ? Math.min(width, phoneMaxWidth) : phoneMaxWidth;
        const scale = width > 0 ? width / cardWidth : 1;
        return {
            scale,
            frameWidth: width > 0 ? cardWidth + sideMargin * 2 : null,
            frameHeight: availableHeight / scale,
            top: 0,
            left: -sideMargin * scale,
            boxHeight: availableHeight,
            fitsHeight: true,
        };
    }

    const reported = reportedHeight ?? desktopReportedHeight;
    const cardHeight = desktopCardHeight(reportedHeight);
    const fits = [1, availableHeight / cardHeight];
    // Too narrow for the desktop layout: render it at its minimum width and shrink to fit.
    if (width > 0) fits.push(width / desktopMinWidth);
    const scale = Math.max(MIN_MEETING_SCALE, Math.min(...fits));
    return {
        scale,
        frameWidth: width > 0 ? width / scale + sideMargin * 2 : null,
        frameHeight: reported,
        top: -desktopTop * scale,
        left: -sideMargin * scale,
        // Rounded down so sub-pixel scaling can't overflow the modal by a fraction of a pixel.
        boxHeight: Math.floor(cardHeight * scale),
        fitsHeight: cardHeight * MIN_MEETING_SCALE <= availableHeight,
    };
};
